<template src="./CreatePersonal.template.html"></template>

<script setup>
import { onMounted, watch, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useCreatePersonal } from '@/composables/usePersonals'
import { ArrowLeft, Save, AlertTriangle, Info } from 'lucide-vue-next'

// Components
import PersonalsTopBar from '../components/personals/PersonalsTopBar.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import Button from '../components/ui/Button.vue'
import Card from '../components/ui/Card.vue'
import Input from '../components/ui/Input.vue'
import Textarea from '../components/ui/Textarea.vue'
import Text from '../components/ui/Text.vue'
import Heading from '../components/ui/Heading.vue'
import LoadingSpinner from '../components/ui/LoadingSpinner.vue'

// Composables
const { t } = useI18n()
const {
  form,
  errors,
  canCreate,
  activeAdsCount,
  validateForm,
  submitForm,
  goBack
} = useCreatePersonal()

// Local state
const titleCharacterCount = ref(0)
const bodyCharacterCount = ref(0)
const submitMessage = ref('')
const submitSuccess = ref(false)

// No categories needed per user request

// Methods
function updateTitleCharacterCount() {
  titleCharacterCount.value = form.value.title.length
}

function updateBodyCharacterCount() {
  bodyCharacterCount.value = form.value.body.length
}

async function handleSubmit() {
  submitMessage.value = ''
  submitSuccess.value = false
  
  const result = await submitForm()
  
  if (result) {
    submitMessage.value = result.message
    submitSuccess.value = result.success
    
    if (!result.success) {
      setTimeout(() => {
        submitMessage.value = ''
      }, 5000)
    }
  }
}

function handleCancel() {
  if (form.value.title || form.value.body) {
    if (confirm(t('personals.confirmDiscard'))) {
      goBack()
    }
  } else {
    goBack()
  }
}

// Watch for character count updates
watch(() => form.value.title, updateTitleCharacterCount)
watch(() => form.value.body, updateBodyCharacterCount)

// Initialize character counts
onMounted(() => {
  updateTitleCharacterCount()
  updateBodyCharacterCount()
})
</script>

<style scoped>
.create-personal {
  min-height: 100vh;
}

.character-counter {
  font-size: 0.75rem;
  color: var(--neutral-500);
}

.character-counter.warning {
  color: var(--accent-coral);
}

.character-counter.danger {
  color: var(--red-500);
}

.success-message {
  background: var(--emerald-50);
  color: var(--emerald-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--emerald-200);
  margin-bottom: 1rem;
}

.error-message {
  background: var(--red-50);
  color: var(--red-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--red-200);
  margin-bottom: 1rem;
}

.info-box {
  background: var(--blue-50);
  color: var(--blue-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--blue-200);
  margin-bottom: 1rem;
}

.warning-box {
  background: var(--amber-50);
  color: var(--amber-700);
  padding: 0.75rem;
  border-radius: 0.5rem;
  border: 1px solid var(--amber-200);
  margin-bottom: 1rem;
}

.form-actions {
  position: sticky;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-top: 1px solid var(--neutral-200);
  padding: 1rem;
  margin: 0 -1rem -1rem -1rem;
}
</style>